type StyledElement = HTMLElement | SVGElement

/**
 * Freeze layout and control values synchronously. SVG serialization does not retain
 * scrollTop/scrollLeft, so reproduce scrolling with transforms in this private copy.
 * The live page is never scrolled, restyled or moved.
 */
export function createCaptureSnapshot(target: HTMLElement): HTMLDivElement {
  const width = window.innerWidth
  const height = window.innerHeight
  const pinned: { clone: StyledElement; rect: DOMRect }[] = []
  const snapshot = document.createElement('div')
  snapshot.setAttribute('data-feedback-capture-ignore', '')
  snapshot.setAttribute('aria-hidden', 'true')
  snapshot.inert = true
  Object.assign(snapshot.style, {
    position: 'fixed',
    left: '0',
    top: '0',
    width: `${width}px`,
    height: `${height}px`,
    overflow: 'hidden',
    pointerEvents: 'none',
    opacity: '0',
    zIndex: '-1',
    background: getComputedStyle(document.body).backgroundColor,
  })

  function copy(source: Node): { node: Node; visible: boolean } | null {
    if (!(source instanceof Element)) return { node: source.cloneNode(), visible: false }
    if (
      source.hasAttribute('data-feedback-capture-ignore') ||
      ['SCRIPT', 'STYLE', 'LINK'].includes(source.tagName)
    )
      return null
    if (!(source instanceof HTMLElement || source instanceof SVGElement)) return null

    const rect = source.getBoundingClientRect()
    const style = getComputedStyle(source)
    const clone =
      source instanceof HTMLTextAreaElement
        ? document.createElement('div')
        : (source.cloneNode(false) as StyledElement)
    for (const property of Array.from(style)) {
      clone.style.setProperty(property, style.getPropertyValue(property))
    }
    clone.style.animation = 'none'
    clone.style.transition = 'none'
    clone.style.caretColor = 'transparent'
    if (
      [style.overflowX, style.overflowY].some(
        (value) => value === 'auto' || value === 'scroll',
      )
    ) {
      // Native scrollbars and offsets are not serialized consistently. Keep the
      // original clipping box; the content translation below reproduces its view.
      clone.style.overflow = 'hidden'
    }
    let visible =
      rect.bottom > 0 && rect.right > 0 && rect.top < height && rect.left < width

    if (style.position === 'sticky') {
      // Sticky offsets depend on a scroll container, which the image no longer has.
      Object.assign(clone.style, {
        position: 'relative',
        top: 'auto',
        bottom: 'auto',
        left: 'auto',
        right: 'auto',
      })
    }
    if (style.position === 'fixed' || style.position === 'sticky')
      pinned.push({ clone, rect })

    if (source instanceof HTMLTextAreaElement) {
      const content = document.createElement('div')
      content.textContent = source.value
      content.style.whiteSpace = 'pre-wrap'
      clone.append(content)
    } else {
      for (const child of source.childNodes) {
        const copied = copy(child)
        if (copied) {
          clone.append(copied.node)
          visible ||= copied.visible
        }
      }
    }

    // Preserve off-screen space instead of removing it and reflowing visible content.
    if (!visible && rect.width > 0 && rect.height > 0) {
      clone.replaceChildren()
      Object.assign(clone.style, {
        visibility: 'hidden',
        boxSizing: 'border-box',
        width: `${rect.width}px`,
        height: `${rect.height}px`,
        minWidth: `${rect.width}px`,
        minHeight: `${rect.height}px`,
      })
      return { node: clone, visible: false }
    }

    if (source instanceof HTMLInputElement && clone instanceof HTMLInputElement) {
      if (source.type !== 'file') clone.value = source.value
      clone.setAttribute('value', source.type === 'file' ? '' : source.value)
      clone.checked = source.checked
      clone.toggleAttribute('checked', source.checked)
    }
    if (source instanceof HTMLSelectElement && clone instanceof HTMLSelectElement) {
      Array.from(clone.options).forEach((option, index) => {
        option.selected = source.options[index]?.selected ?? false
        option.toggleAttribute('selected', option.selected)
      })
    }
    if (source instanceof HTMLImageElement && clone instanceof HTMLImageElement) {
      clone.removeAttribute('srcset')
      clone.removeAttribute('sizes')
      clone.src = source.currentSrc || source.src
      clone.loading = 'eager'
    }
    if (source instanceof HTMLCanvasElement && clone instanceof HTMLCanvasElement) {
      clone.getContext('2d')?.drawImage(source, 0, 0)
    }

    if (source.scrollTop || source.scrollLeft) {
      for (const child of Array.from(clone.childNodes)) {
        if (child instanceof HTMLElement || child instanceof SVGElement) {
          translate(child, -source.scrollLeft, -source.scrollTop)
        } else if (child.nodeType === Node.TEXT_NODE && child.textContent?.trim()) {
          const span = document.createElement('span')
          span.textContent = child.textContent
          Object.assign(span.style, {
            position: 'relative',
            left: `${-source.scrollLeft}px`,
            top: `${-source.scrollTop}px`,
          })
          child.replaceWith(span)
        }
      }
    }
    return { node: clone, visible }
  }

  const copied = copy(target)?.node
  if (!(copied instanceof HTMLElement))
    throw new Error('Der Seiteninhalt konnte nicht erfasst werden.')
  const rect = target.getBoundingClientRect()
  Object.assign(copied.style, {
    position: 'absolute',
    margin: '0',
    left: `${rect.left}px`,
    top: `${rect.top}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
    transform: 'none',
  })
  snapshot.append(copied)
  document.body.append(snapshot)
  try {
    for (const entry of pinned) {
      if (!entry.clone.isConnected) continue
      const clonedRect = entry.clone.getBoundingClientRect()
      translate(
        entry.clone,
        entry.rect.left - clonedRect.left,
        entry.rect.top - clonedRect.top,
      )
    }
    return snapshot
  } catch (error) {
    snapshot.remove()
    throw error
  }
}

function translate(element: StyledElement, x: number, y: number) {
  const original = element.style.transform
  element.style.transform =
    `translate(${x}px, ${y}px) ${original === 'none' ? '' : original}`.trim()
}
